/**
 * Store Pinia des réservations (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import ReservationService from "../services/ReservationService";
import { IReservation } from "../models/reservation";
import { IReservationApiResponse } from "../models/api/reservationApiResponse";
import { IParameter } from "../models/parameter";
import { IReservationFilter } from "../models/filters/reservationFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { IReservationForm } from "../models/forms/reservationForm";
import DataOperationError from "../models/api/dataOperationError";
import { useParameterStore } from "./parameter.store";
import { useBoars } from "./boars.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

/**
 * Store Pinia des réservations utilisant la Composition API
 */
export const useReservations = defineStore('reservations', () => {
    // State
    const datas = ref<IReservation[]>([]);
    const causes = ref<IParameter[]>([]);
    const itemForm = ref<IReservationForm>({} as IReservationForm);
    const current = ref<IReservation>({} as IReservation);

    // Getters
    const doesAnimalHaveReservation = computed(() => (animalId: number): boolean => {
        return datas.value.some((r) => r.animalId == animalId && r.removeDate == undefined);
    });

    const getById = computed(() => (reservationId: number): IReservation | undefined => {
        return datas.value.find((r) => r.id == reservationId);
    });

    // Actions

    async function load(filter: IReservationFilter) {
        let result = await ReservationService.getAll<IReservationFilter, IReservationApiResponse>(filter);
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateReservations(result.data as IReservationApiResponse[]);
        }

        return result;
    }

    /**
     * Hydrate une liste de réservations en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param reservations Liste de réservations reçues de l'API avec des IDs
     * @returns Liste de réservations avec les références des stores
     */
    async function hydrateReservations(reservations: IReservationApiResponse[]): Promise<IReservation[]> {
        if (reservations.length === 0) return [];

        // 1. Récupérer les stores une seule fois
        const parameterStore = useParameterStore();
        const boarsStore = useBoars();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];

        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps d'index pour des lookups O(1)
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const boarsMap = new Map(boarsStore.datas.map(b => [b.id, b]));

        // 4. Hydrater toutes les réservations en une seule passe
        const models: IReservation[] = [];

        for (const reservation of reservations) {
            const model = { ...reservation } as unknown as IReservation;

            // Lier les références (lookup O(1) via Map)
            // Fallback sur l'objet API si l'animal n'est pas dans le store (ex: filtre centre)
            model.animal = boarsMap.get(reservation.animalId) ?? model.animal;
            model.reason = parameterMap.get(reservation.reasonId) ?? model.reason;

            models.push(model);
        }

        return models;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IReservation);
        return current.value;
    }

    async function loadCauses() {
        let result = await ReservationService.pathParamApiListCall<IParameter>(["causes"]);
        causes.value = [];

        if (!result.error) {
            causes.value = result.data as IParameter[];
        }

        return result;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IReservationApiResponse | IDataOperationResult<IReservationApiResponse>>;

        if (itemForm.value.id > 0) {
            result = await ReservationService.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateReservations([result.data as IReservationApiResponse]);
                current.value = modelData;
                let index = datas.value.findIndex((a) => a.id == itemForm.value.id);

                if (index >= 0) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = "Item non trouvé dans le store";
                    return new ServiceOperationResult<IDataOperationResult<IReservation>>(true, { errors: error });
                }
            }
        } else {
            result = await ReservationService.create(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateReservations([result.data as IReservationApiResponse]);
                current.value = modelData;
                // SignalR peut avoir déjà ajouté l'item avant la réponse HTTP (race condition via _hub.CreateItem)
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    async function remove() {
        let result = {} as ServiceOperationResult<IReservationApiResponse | IDataOperationResult<IReservationApiResponse>>;

        result = await ReservationService.pathParamApiCall([`${itemForm.value.id}`], 'patch');

        if (!result.error) {
            let removedIndex = datas.value.findIndex((r: IReservation) => r.id == itemForm.value.id);
            datas.value.splice(removedIndex, 1);
        }
    }

    function toForm(item: IReservation): IReservationForm {
        const form = {} as IReservationForm;
        Object.assign(form, item);

        form.animalId = item.animal?.id ?? item.animalId;
        form.reasonId = item.reason?.id ?? item.reasonId;

        return form;
    }

    function newForm(): IReservationForm {
        itemForm.value = {
            id: 0,
            animalId: undefined,
            issueDate: undefined,
            reasonId: undefined,
            comment: undefined,
            removeDate: undefined,
            lastGenerationDate: undefined
        } as IReservationForm;

        return itemForm.value;
    }

    // Retourner les propriétés et méthodes publiques
    return {
        // State
        datas,
        causes,
        itemForm,
        current,

        // Getters
        doesAnimalHaveReservation,
        getById,

        // Actions
        load,
        hydrateReservations,
        searchStoreById,
        loadCauses,
        save,
        remove,
        toForm,
        newForm,
    };
});

signalRManager.connection.on("MajReservation", async (payload: IPayloadStore) => {
    const store = useReservations();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateReservations([payload.data as IReservationApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateReservations([payload.data as IReservationApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
